import type {
  ColumnAggregateType,
  ScorecardCompareOperator,
  ScorecardCompareType,
} from "../../criterionConfig";

import {
  SCORECARD_DEFAULT_THRESHOLDS,
  type ScorecardThresholds,
} from "@/queries/smart-tables/scorecard";
import type { LanguageType } from "@/components/CodeEditor/types";

export const NO_LABEL_COLUMN_VALUE = "__none__";
export const CODE_CHECK_LANGUAGES: LanguageType[] = ["PYTHON", "JAVASCRIPT"];

export const COMPARE_TYPE_COPY: Record<ScorecardCompareType, { label: string; helper: string }> = {
  STRING: {
    label: "Simple String (==)",
    helper: "Trims string values and checks whether the source and target are exactly equal.",
  },
  NUMBER: {
    label: "Number",
    helper: "Parses both values as numbers, then applies the selected operator.",
  },
  JSON: {
    label: "JSON Path",
    helper: "Parses both values as JSON, selects the same JSON path, and compares the results.",
  },
};

export const COMPARE_OPERATOR_COPY: Record<
  ScorecardCompareOperator,
  { label: string; helper: string }
> = {
  EQ: {
    label: "Equals (=)",
    helper: "Passes when the source number equals the target number.",
  },
  NE: {
    label: "Does not equal (≠)",
    helper: "Passes when the source number differs from the target number.",
  },
  GT: {
    label: "Greater than (>)",
    helper: "Passes when the source number is greater than the target number.",
  },
  GTE: {
    label: "Greater than or equal (≥)",
    helper: "Passes when the source number is greater than or equal to the target number.",
  },
  LT: {
    label: "Less than (<)",
    helper: "Passes when the source number is less than the target number.",
  },
  LTE: {
    label: "Less than or equal (≤)",
    helper: "Passes when the source number is less than or equal to the target number.",
  },
};

export const columnAggregateCopy: Record<ColumnAggregateType, { label: string; helper: string }> = {
  average_value: {
    label: "Average numeric value",
    helper: "Best for rating or numeric score columns, like a 1–5 scale.",
  },
  most_frequent_value: {
    label: "Most frequent value",
    helper: "Best for label, category, or winner columns.",
  },
  min_value: {
    label: "Lowest numeric value",
    helper: "Find the smallest number in a column, optionally showing a label from that row.",
  },
  max_value: {
    label: "Highest numeric value",
    helper: "Find the largest number in a column, optionally showing a label from that row.",
  },
  percentile_value: {
    label: "Percentile",
    helper: "Like p50 or p95 latency.",
  },
  mean_absolute_error: {
    label: "Mean absolute error (MAE)",
    helper: "Average miss vs. expected. Lower is better.",
  },
  root_mean_squared_error: {
    label: "Root mean squared error (RMSE)",
    helper: "Like MAE, but weights big misses more.",
  },
  classification_report: {
    label: "Classification report",
    helper: "Accuracy, precision, recall, F1, confusion matrix.",
  },
};

const parseVariablesFromTemplate = (template: string): string[] => {
  const variables = new Set<string>();
  const regex = /\{(\w+)\}/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(template)) !== null) {
    variables.add(match[1]);
  }
  return Array.from(variables);
};

export const extractVariablesFromAssertions = (prompt: string): string[] => {
  try {
    const parsed = JSON.parse(prompt);
    if (Array.isArray(parsed)) {
      const variables = new Set<string>();
      parsed.forEach(assertion => {
        parseVariablesFromTemplate(String(assertion)).forEach(variable => variables.add(variable));
      });
      return Array.from(variables);
    }
  } catch {
    // Treat non-JSON prompts as a single assertion.
  }

  return parseVariablesFromTemplate(prompt);
};

export const parseAssertionsFromPrompt = (prompt: string): string[] => {
  if (!prompt) {
    return [""];
  }

  try {
    const parsed = prompt.startsWith("[") ? JSON.parse(prompt) : null;
    if (Array.isArray(parsed)) {
      return parsed.map(assertion => String(assertion));
    }
  } catch {
    // Fall back to the raw prompt below.
  }

  return [prompt];
};

export const normalizeThresholds = (thresholds?: ScorecardThresholds): ScorecardThresholds => ({
  pass: thresholds?.pass ?? SCORECARD_DEFAULT_THRESHOLDS.pass,
  warn: thresholds?.warn ?? SCORECARD_DEFAULT_THRESHOLDS.warn,
});
